/**
 * `<NodeDetailDrawer/>` — the node detail drawer (B2.5).
 *
 * Issue #760 (Phase 2). A right-side drawer that slides in over the **live,
 * dimmed** Map (a scrim sits between the editor and the drawer, so you keep
 * your place — the gallery dims the tree to 0.35 and highlights the open
 * node). Header (title + ✕), the current-state chip + a "✦ change ▾" control
 * (opens the B2.8 `ChangeStateMenu`), and the tabs. The tab bar shows only
 * tabs that DO something (#930): **Details** and **History** are base (always
 * present, deliberate); **Notes** (`task_notes`) and **Reminders**
 * (`task_reminders`) are gated layers that are cleanly ABSENT when their
 * component is off — never a do-nothing stub tab.
 *
 *   - **Details** — leads with the FOCAL summary (#930): title (header) + state
 *     (the chip above the tabs) + the "Assigned to" row (opens the B2.6
 *     `AssignPopover`), then the editable description (PATCH) and the low-key
 *     optional Target date + Estimate rows (display-only — no write-path yet;
 *     §A1.2 demotes estimates). The secondary STRUCTURE — the read-only "Part
 *     of" parent row (with a light re-parent cue, #896), the dependency "Comes
 *     after / Blocks" rows (when #766 is on), and the SUB-TASKS list with
 *     "+ sub-task" — sits in ONE clearly-labelled, collapsible "Structure &
 *     connections" group (defaults open). A calm "Remove from map" action
 *     (#896) closes the tab — a soft, recoverable archive that keeps history
 *     (§A6), the one obvious place to take a task off the map.
 *   - **Notes** — the per-node feedback thread (B4.1, issue #767); present only
 *     when `task_notes` is on, else the tab is absent (#930).
 *   - **History** — the per-node provenance feed (B3.1, issue #763); base
 *     infra, always present, and degrades calmly to "No activity yet." when
 *     there's no data (no empty shell).
 *
 * The drawer is presentational over the shared data: it reads the live
 * `TreeNode` from the editor's one `useTaskNodes` source and fires the same
 * mutations the canvas/outline use (one source of state/assign logic). §A8:
 * the visible "who can see this" line stays in the footer.
 *
 * Editorial revamp (#1043, Wave 1 B2.5): the drawer chrome + its Details tab
 * are theme-aware. With the per-team `task_editorial_ui` flag OFF (every team,
 * today) the drawer renders byte-identical to the shipped soft-mint look; with
 * it ON it renders the wireframe `.t-drawer`: a cream surface with a serif
 * masthead title + mono kicker, an underline tab bar, and `k`/`v` metadata
 * rows with dashed separators + mono labels. The sub-task list is enriched into
 * the wireframe's `ChildCard` (per-child state chip + assignees + target + deps
 * + a "N of M sub-tasks done · 0 blocked · stays in progress until all done"
 * roll-up) — a richer DISPLAY of data the drawer already holds, no new fetch.
 * Same JSX scaffold, same data-testids, same copy, same tabs + behaviour — only
 * the look forks on theme (RE-SKIN, not rebuild). Status chips/dots + avatars
 * come from the shared theme-aware atoms (Wave 1 PR-A), never re-derived here.
 */

import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { TaskTimingRow, type TimingPatchHandler } from "./TaskTimingRow";

import { useTeam } from "../../api/useTeam";
import { BottomSheet } from "../../components/ui/BottomSheet";
import { type HistoryScope, useNodeHistory } from "../api/useNodeHistory";
import { useAddNodeNote, useNodeNotes, useRemoveNodeNote } from "../api/useNodeNotes";
import { useNoteSubscription, useSetNoteSubscription } from "../api/useNoteSubscription";
import { useClearReminder, useNodeReminders, useSetReminder } from "../api/useReminders";
import type { TaskDependency } from "../api/useTaskDependencies";
import { FullHistoryPanel } from "../history/FullHistoryPanel";
import { NodeHistoryView } from "../history/NodeHistoryView";
import type { TaskMember } from "../members/useTaskTeamMembers";
import { useMapTheme } from "../theme/MapThemeContext";
import type { MapTheme } from "../theme/tokens";
import type { TreeNode } from "../tree/buildTree";
import type { AuthorReason } from "../tree/canAuthor";
import { blocks, comesAfter, wouldCreateDependencyCycle } from "../tree/dependencyGraph";
import {
  ESTIMATE_BUCKETS,
  MINUTES_PER_DAY,
  estimateRollup,
  formatEstimate,
} from "../tree/estimates";
import { PALETTE } from "../tree/palette";
import { type TaskState, taskStateStyle } from "../tree/state";
import { AssignPopover } from "./AssignPopover";
import { AvatarRow } from "./Avatar";
import { ChangeStateMenu } from "./ChangeStateMenu";
import { NotesTab } from "./NotesTab";
import { RemindersTab } from "./RemindersTab";
import { StatusChip, StatusDot } from "./StatusChip";

export interface NodeDetailDrawerProps {
  /** The team — for the History tab's `useNodeHistory` fetch (#763). */
  teamId: string | undefined;
  /** The open node (live, from the editor's one data source). */
  node: TreeNode;
  /** The node's parent, if any — drives the "Part of" row. */
  parent: TreeNode | null;
  /** The full team roster (for assignees + the assign popover). */
  members: TaskMember[];
  /**
   * node id → title, so the History tab can name referenced nodes ("created
   * under '…'", "comes after '…'"). Optional: an unknown id degrades to a
   * generic phrase, never a crash.
   */
  nodeTitleById?: Map<string, string> | undefined;
  /**
   * Whether the current actor may author this node (§A5 recursive ownership,
   * via the `canAuthor` helper). When false, the drawer is READ-ONLY: it shows
   * the same content but hides every mutating control (rename, change-state,
   * assign, edit description, + sub-task) — so we never paint a button the API
   * would 403. Defaults to `true` (the lead editor, where authorship is given).
   */
  canAuthor?: boolean;
  /**
   * WHY the viewer may (or may not) author this node — the §A5 reason, used
   * ONLY to surface the model calmly + factually in the footer ("you can edit
   * this — you're assigned" / "editing is open to the lead and anyone assigned
   * …"). It changes NO behaviour: the mutating controls stay gated on
   * `canAuthor`; this is a label, not a check. Defaults to `undefined` →
   * the footer falls back to the prior generic ownership line for an author
   * and shows no read-only explanation (byte-unchanged for any caller that
   * doesn't pass it, e.g. legacy tests). `MemberMapView` derives it from the
   * shared tree via `canAuthorReason`; the lead editor passes `"lead"`.
   */
  authorReason?: AuthorReason;
  /**
   * The dependency layer (#766, gated behind `task_dependencies`, §A10). When
   * provided the Details tab shows the "Comes after / Blocks" rows (B2.7) and,
   * for an author, an "+ add" picker; when omitted (gate off) those rows are
   * absent and the drawer is byte-unchanged. Whole-team visible (§A8) — the
   * rows render for members too; only the add/remove controls are gated on
   * authorship.
   */
  dependencies?: readonly TaskDependency[];
  /**
   * Every node on the team (id + title + state), for naming dependency
   * endpoints + populating the add-dependency picker. Required only when
   * `dependencies` is supplied.
   */
  allNodes?: readonly { id: string; title: string; state: TaskState }[];
  /**
   * Whether the notes layer (#767, `task_notes`, §A10) is on for this team.
   * When true the Notes tab renders the real per-node feedback thread (B4.1) +
   * compose box (fetching its own data via `useNodeNotes`, gated on the tab
   * being open — the same lazy pattern the History tab uses); when false (gate
   * off) the tab shows the calm placeholder. Whole-node-branch visible (§A8):
   * the thread renders for members too; the per-note delete affordance is
   * scoped to the author or a lead (§A9 firewall).
   */
  notesEnabled?: boolean;
  /**
   * Whether the reminders layer (#768, `task_reminders`, §A10) is on for this
   * team. When true the drawer gains a Reminders tab where the actor can SET a
   * forward nudge on this node (a future date + optional target + optional note)
   * and clear ones they may (setter or lead); it fetches its own list lazily
   * (gated on the tab being open — the same pattern as Notes / History). When
   * false (gate off) the tab is absent and the drawer is byte-unchanged. A
   * reminder is a FORWARD nudge — never time-logging, never a tally (§A1.3 / §A9).
   */
  remindersEnabled?: boolean;
  /**
   * The signed-in actor's membership id on this team (null if unresolved).
   * Used ONLY to decide which notes show a delete affordance (your own) and to
   * resolve "is this mine" — never displayed, never a per-person metric (§A9).
   */
  currentMembershipId?: string | null;
  /**
   * Whether the actor is the team lead / co_lead. A lead may remove any note
   * (mirrors the backend's author-or-lead 403 rule); a member only their own.
   */
  isLead?: boolean;
  /**
   * Deep-link hint (#907): open the drawer on this tab. Currently only "notes"
   * (a notification click lands on the Notes tab). Applied once per (re)open of
   * the drawer's node; a later manual tab change is not fought. Omit / null for
   * the default ("details").
   */
  initialTab?: "notes" | null | undefined;
  /**
   * Deep-link hint (#907): a note id to scroll to / briefly highlight in the
   * Notes tab. Passed through to `<NotesTab/>`; cleared via `onFocusConsumed`
   * once the highlight has been applied.
   */
  focusNoteId?: string | null | undefined;
  /** Called once the `focusNoteId` highlight has been applied (clears the hint). */
  onFocusConsumed?: (() => void) | undefined;
  onClose: () => void;
  onRename: (title: string) => void;
  onSetDescription: (description: string) => void;
  onSetState: (state: TaskState) => void;
  /**
   * Set or clear this node's optional target date (B2.11, #1043). `date` is an
   * ISO `YYYY-MM-DD` string, or `null` to clear it. Target date is a CORE Map
   * capability (no per-component toggle), but the set/clear PICKER renders ONLY
   * in editorial mode and ONLY for an author; when this handler is omitted (or
   * legacy), the "Target date" row stays display-only and byte-unchanged.
   */
  onSetTargetDate?: (date: string | null) => void;
  /** #2631 - set or clear the start time, minutes or place. Omitted: read-only. */
  onSetTiming?: TimingPatchHandler;
  /**
   * Whether the `task_estimates` layer (B2.12, #1043) is on for this team. When
   * on (AND editorial AND author), the "Estimate" row becomes a coarse-bucket
   * PICKER (½d…2w + custom days), and a parent shows the read-time sum-of-leaves
   * roll-up; when off the row stays display-only and byte-unchanged. Defaults
   * off, so a team without the layer sees the drawer exactly as before.
   */
  estimatesEnabled?: boolean;
  /**
   * Set or clear this node's optional coarse estimate (B2.12). `minutes` is the
   * bucket value in minutes, or `null` to clear it. Only meaningful when the
   * `task_estimates` layer is on; when omitted the picker is absent.
   */
  onSetEstimate?: (minutes: number | null) => void;
  onAddSubtask: () => void;
  onAssignToggle: (membershipId: string, assign: boolean) => void;
  /**
   * Remove (soft-ARCHIVE) this node from the map (#896). The drawer surfaces it
   * as the one obvious "Remove from map" action; it is recoverable — history /
   * provenance is preserved, never a hard delete (§A6). Only wired for an
   * author (§A5); when omitted, the action is absent. The boolean is `cascade`
   * (#1043): a LEAF removes the one node (false); a PARENT removes the whole
   * live branch in one recoverable action (true) — resolving the #1087 follow-up.
   */
  onArchive?: (cascade?: boolean) => void;
  /** Open another node's drawer (e.g. a sub-task row, the "Part of" parent). */
  onOpenNode: (id: string) => void;
  /**
   * Add a `from ──▶ to` dependency edge (#766). The drawer fires this from the
   * "Comes after" picker (target = THIS node) and the "Blocks" picker (source =
   * THIS node). Rejecting cycles / handling the 422 lives upstream (the editor).
   */
  onAddDependency?: (fromNodeId: string, toNodeId: string) => void;
  /** Remove a dependency edge by id (#766). */
  onRemoveDependency?: (dependencyId: string) => void;
  /**
   * How the drawer presents (#1043, Wave 6 / M3). The default `"drawer"` is the
   * desktop right-side `aside` + scrim, byte-unchanged. `"sheet"` (mobile +
   * editorial only) renders the SAME inner content inside the shared mobile
   * `<BottomSheet/>` (slides up from the bottom edge) with a bottom action grid
   * (◐ change · + sub-task · ⇄ assign · ⋯ more) — the wireframe `V_M3`. The
   * content (header / state / tabs / body / footer) is identical; only the shell
   * differs. The caller picks `"sheet"` when `useIsMobile()` is true.
   */
  presentation?: "drawer" | "sheet";
}

type DrawerTab = "details" | "notes" | "reminders" | "history";

export function NodeDetailDrawer(props: NodeDetailDrawerProps) {
  const {
    teamId,
    node,
    parent,
    members,
    nodeTitleById,
    onClose,
    canAuthor = true,
    authorReason,
    notesEnabled = false,
    remindersEnabled = false,
    estimatesEnabled = false,
    currentMembershipId = null,
    isLead = false,
    initialTab = null,
    focusNoteId = null,
    onFocusConsumed,
    presentation = "drawer",
  } = props;
  const [tab, setTab] = useState<DrawerTab>(initialTab === "notes" ? "notes" : "details");

  // The per-task timeline's scope (the founder's headline behaviour): default
  // "subtree" — this task AND everything below it — with a one-click narrowing
  // to "this task only". Lives here (one `useNodeHistory` source); the History
  // tab view drives it. `fullHistory` opens the prominent full-panel timeline.
  const [historyScope, setHistoryScope] = useState<HistoryScope>("subtree");
  const [fullHistory, setFullHistory] = useState(false);

  // #907 deep-link: when the drawer (re)opens on a different node WITH a tab
  // hint (a notification click), jump to that tab. Keyed on the node id + the
  // hint so a manual tab change afterwards isn't fought (the effect only re-runs
  // when the target node / hint itself changes).
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-apply on node / hint change only
  useEffect(() => {
    if (initialTab === "notes") setTab("notes");
  }, [node.id, initialTab]);

  // B3.1/B3.2 — the node's provenance stream. Fetches only while the History
  // tab is active (so opening a drawer never fires the request until needed,
  // and switching nodes re-keys cleanly). The endpoint is base temporal infra,
  // not component-gated; the view degrades calmly on a 404 (undeployed).
  const history = useNodeHistory(
    tab === "history" ? teamId : undefined,
    tab === "history" ? node.id : undefined,
    historyScope,
  );

  // B4.1 (#767) — the node's note thread. Like history, it fetches only while
  // the Notes tab is active AND the layer is on (`task_notes`), so opening a
  // drawer fires no notes request until needed; switching nodes re-keys
  // cleanly. The mutations live here too (one `useNodeNotes` source per node,
  // §A7); the backend enforces the gate + the author/lead delete rule.
  const notesActive = tab === "notes" && notesEnabled;
  const notes = useNodeNotes(notesActive ? teamId : undefined, notesActive ? node.id : undefined);
  const addNote = useAddNodeNote(teamId, node.id);
  const removeNote = useRemoveNodeNote(teamId, node.id);

  // #907 — the caller's per-node note subscription ("Notify me of notes on this
  // task"). Like the thread, the GET hydrates only while the Notes tab is open
  // AND the layer is on, so opening a drawer fires no subscription request until
  // the toggle is shown; the PUT/DELETE mutation lives here (one source per
  // node, §A7). The backend enforces the gate; the toggle degrades to "off" on
  // a 404 / errored body.
  const subscription = useNoteSubscription(
    notesActive ? teamId : undefined,
    notesActive ? node.id : undefined,
  );
  const setSubscription = useSetNoteSubscription(teamId, node.id);

  // #906 — the team's IANA timezone, used to render note timestamps as
  // date + HH:MM in the team's local time. Shares the cached `useTeam`
  // query the rest of the SPA already warms; falls back to the viewer's
  // zone (undefined) until the team metadata resolves.
  const teamTimezone = useTeam(teamId).data?.timezone;

  // B4.2 (#768) — this node's reminders. Like notes, the list fetches only
  // while the Reminders tab is active AND the layer is on (`task_reminders`),
  // so opening a drawer fires no reminders request until needed; switching
  // nodes re-keys cleanly. The set + clear mutations live here (one
  // `useReminders` source per node, §A7); both invalidate the team bell too so
  // a nudge set / cleared here updates the bell. The backend enforces the gate
  // + the setter/lead clear rule.
  const remindersActive = tab === "reminders" && remindersEnabled;
  const reminders = useNodeReminders(
    remindersActive ? teamId : undefined,
    remindersActive ? node.id : undefined,
  );
  const setReminder = useSetReminder(teamId, node.id);
  const clearReminder = useClearReminder(teamId);

  // Esc steps back one level: it closes the full-panel timeline if open, else
  // closes the drawer (a standard, calm dismissal).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (fullHistory) setFullHistory(false);
      else onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, fullHistory]);

  // Switching the drawer to a different node closes the full-panel timeline (it
  // belongs to the node it was opened for). The scope choice is a preference, so
  // it deliberately persists across nodes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on node change only
  useEffect(() => {
    setFullHistory(false);
  }, [node.id]);

  const blocked = node.state === "blocked";

  // Editorial revamp (#1043 Wave 1): the drawer chrome forks its LOOK on the
  // per-team theme flag. Legacy stays byte-identical to today; editorial paints
  // the wireframe `.t-drawer` (cream surface, hairline left rule). All structure
  // / testids / behaviour are unchanged either way.
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";

  // The drawer's inner content (header → state → tabs → tab body → footers). It
  // is identical in both presentations (#1043 / M3): the desktop right-side
  // `aside` and the mobile `<BottomSheet/>` both render exactly this. Only the
  // surrounding shell (scrim + panel vs. sheet) differs, so the content never
  // forks. Extracted as a const so the two shells share one source of truth.
  const drawerInner = (
    <>
      <DrawerHeader
        title={node.title}
        state={node.state}
        canAuthor={canAuthor}
        onClose={onClose}
        onRename={props.onRename}
        theme={theme}
      />

      <StateRow
        state={node.state}
        canAuthor={canAuthor}
        onSetState={props.onSetState}
        theme={theme}
        // #767 seam: when the notes layer is on, a Blocked reason typed in
        // the change-state menu is persisted as a note on this node. The view
        // jumps to the Notes tab so the new note is visible. When off, the
        // reason step degrades to its calm placeholder (no callback).
        onSaveReason={
          notesEnabled
            ? (text) => {
                addNote.mutate({ body: text });
                setTab("notes");
              }
            : undefined
        }
      />

      {/* Tabs — the bar shows only tabs that DO something (#930). Details +
            History are base (always present, deliberate); Notes + Reminders are
            gated layers that are cleanly ABSENT when their component is off (no
            dead/stub tab). The two base tabs degrade calmly on no data: History
            shows "No activity yet." rather than an empty shell. */}
      <div
        className={
          editorial ? "flex items-center px-5" : "flex items-center gap-2 border-b px-5 pt-1"
        }
        style={{ borderColor: editorial ? c.rule : PALETTE.cardBorder, borderBottomWidth: 1 }}
      >
        <TabButton id="details" active={tab} onSelect={setTab} theme={theme}>
          Details
        </TabButton>
        {/* B4.1 (#767) — the Notes tab appears only when `task_notes` is on
              for the team; off, the drawer carries no Notes tab at all (no calm
              stub, #930) and the layer reads as "later" via the team's optional
              features, not a tab that does nothing. The count is about THIS
              node's feedback (the work), never a tally about a person (§A9), and
              resolves only while the tab is open (the thread fetches lazily). */}
        {notesEnabled && (
          <TabButton id="notes" active={tab} onSelect={setTab} theme={theme}>
            {notes.notes.length > 0 ? `Notes (${notes.notes.length})` : "Notes"}
          </TabButton>
        )}
        {/* B4.2 (#768) — the Reminders tab appears only when `task_reminders`
              is on for the team; off, the drawer is byte-unchanged (no extra
              tab). The count is about THIS node's nudges (the work), never a
              tally about a person (§A9), and resolves only while the tab is
              open (the list fetches lazily). */}
        {remindersEnabled && (
          <TabButton id="reminders" active={tab} onSelect={setTab} theme={theme}>
            {reminders.reminders.length > 0
              ? `Reminders (${reminders.reminders.length})`
              : "Reminders"}
          </TabButton>
        )}
        <TabButton id="history" active={tab} onSelect={setTab} theme={theme}>
          History
        </TabButton>
      </div>

      {/* Tab body */}
      <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
        {tab === "details" && (
          <DetailsTab
            node={node}
            parent={parent}
            members={members}
            blocked={blocked}
            canAuthor={canAuthor}
            notesEnabled={notesEnabled}
            theme={theme}
            onShowNotes={() => setTab("notes")}
            dependencies={props.dependencies}
            allNodes={props.allNodes}
            onSetDescription={props.onSetDescription}
            onSetTargetDate={props.onSetTargetDate}
            onSetTiming={props.onSetTiming}
            estimatesEnabled={estimatesEnabled}
            onSetEstimate={props.onSetEstimate}
            onAddSubtask={props.onAddSubtask}
            onAssignToggle={props.onAssignToggle}
            onOpenNode={props.onOpenNode}
            onAddDependency={props.onAddDependency}
            onRemoveDependency={props.onRemoveDependency}
            onArchive={props.onArchive}
          />
        )}
        {tab === "notes" && notesEnabled && (
          // #767 — the real per-node feedback thread (B4.1). The Notes tab
          // only exists when `task_notes` is on (#930), so there is no gated
          // placeholder body to fall back to — an absent layer reads as "later"
          // via the team's optional features, never a tab that does nothing.
          <NotesTab
            notes={notes.notes}
            isLoading={notes.isLoading}
            isError={notes.isError}
            members={members}
            currentMembershipId={currentMembershipId}
            isLead={isLead}
            onAdd={(body, mentions) => addNote.mutate({ body, mentions })}
            isAdding={addNote.isPending}
            onRemove={(noteId) => removeNote.mutate(noteId)}
            teamTimezone={teamTimezone}
            // #907 — the per-node note-subscription opt-in (@mentions always
            // notify; this is the additional opt-in for a followed task).
            subscription={{
              subscribed: subscription.subscribed,
              busy: subscription.isLoading || setSubscription.isPending,
              onToggle: (subscribe) => setSubscription.mutate(subscribe),
            }}
            // #907 deep-link — scroll to + briefly highlight a referenced note.
            focusNoteId={focusNoteId}
            onFocusConsumed={onFocusConsumed}
          />
        )}
        {tab === "reminders" && remindersEnabled && (
          // #768 — set + list forward nudges on this node (B4.2, node-scoped).
          <RemindersTab
            reminders={reminders.reminders}
            isLoading={reminders.isLoading}
            isError={reminders.isError}
            members={members}
            currentMembershipId={currentMembershipId}
            isLead={isLead}
            onSet={(args) => setReminder.mutate(args)}
            isSetting={setReminder.isPending}
            onClear={(reminderId) => clearReminder.mutate(reminderId)}
          />
        )}
        {tab === "history" && (
          <NodeHistoryView
            nodeTitle={node.title}
            nodeId={node.id}
            events={history.events}
            isLoading={history.isLoading}
            isError={history.isError}
            members={members}
            nodeTitleById={nodeTitleById}
            scope={historyScope}
            onScopeChange={setHistoryScope}
            onOpenFullView={() => setFullHistory(true)}
            teamTimezone={teamTimezone}
          />
        )}
      </div>

      {/* Footer: the §A5 permission cue — a calm, factual line that makes the
            assignee-based model APPARENT (#1043 item 4). For an author it states
            WHY they can edit (lead / assigned here / assigned above); for a
            read-only viewer it explains who CAN edit + how to join in, so a
            member never hits a silent "why can't I edit?" wall. Never blame,
            never a per-person metric (§A9) — it describes the rule, not a
            person. Then the §A8 "who can see this" privacy line. */}
      {(() => {
        const cue = permissionCueText(canAuthor, authorReason);
        if (!cue) return null;
        return (
          <div
            className="border-t px-5 pt-3 pb-1 text-[11px] italic"
            style={{
              borderColor: editorial ? c.rule : PALETTE.cardBorder,
              color: editorial ? c.faint : PALETTE.faint,
            }}
            data-testid={canAuthor ? "drawer-ownership-line" : "drawer-readonly-line"}
          >
            {cue}
          </div>
        );
      })()}
      <div
        className="border-t px-5 py-3 text-[11px]"
        style={{
          borderColor: editorial ? c.rule : PALETTE.cardBorder,
          color: editorial ? c.faint : PALETTE.faint,
        }}
        data-testid="drawer-visibility-line"
      >
        who can see this: everyone on the team
      </div>
    </>
  );

  // The full-panel timeline (B1) + the slide-in keyframe are shared chrome that
  // floats above either shell. Defined once so both presentations reuse them.
  const sharedOverlays = (
    <>
      {/* The prominent full-panel timeline (B1) — opened from the History tab's
          "⤢ Full view". A full-screen takeover above the drawer; "← Back"
          returns. Self-contained (owns its own fetch + scope), so it works
          identically for a lead and a member. */}
      {fullHistory && teamId && (
        <FullHistoryPanel
          teamId={teamId}
          nodeId={node.id}
          nodeTitle={node.title}
          nodeState={node.state}
          parentTitle={parent?.title ?? null}
          members={members}
          nodeTitleById={nodeTitleById}
          teamTimezone={teamTimezone}
          initialScope={historyScope}
          onClose={() => setFullHistory(false)}
        />
      )}

      <style>
        {
          "@keyframes drawerIn { from { transform: translateX(16px); opacity: 0.6; } to { transform: translateX(0); opacity: 1; } }"
        }
      </style>
    </>
  );

  // ── M3 (mobile + editorial): present the SAME content in the shared mobile
  // `<BottomSheet/>` (slides up from the bottom edge) instead of the right-side
  // drawer. The sheet supplies the grab-handle + scrim + focus-trap + Esc; we
  // add the wireframe `V_M3` bottom action grid (◐ change · + sub-task ·
  // ⇄ assign · ⋯ more) for an author. The chrome differs; the content is
  // identical (RE-SKIN, not rebuild). The caller only passes "sheet" on a phone.
  if (presentation === "sheet") {
    return (
      <>
        <BottomSheet
          open
          onOpenChange={(next) => {
            if (!next) onClose();
          }}
          ariaLabel={`${node.title} details`}
          className="bg-bg-elevated"
        >
          <div
            data-testid="node-detail-sheet"
            data-node-id={node.id}
            className="flex max-h-[85vh] flex-col"
            style={{
              backgroundColor: c.cardBg,
              fontFamily: theme.fonts.sans,
            }}
          >
            {drawerInner}
            {/* The wireframe `V_M3` bottom action grid — a phone-thumb reach for
                the drawer's primary verbs. Author-only (a non-author drawer is
                read-only, §A5), so it mirrors the controls already inside the
                content rather than offering a 403. Each tile routes to the
                existing handler: ◐ change state (jump to Details where the chip +
                change menu live), + sub-task, ⇄ assign (Details), ⋯ more
                (History). Mono glyphs per the wireframe. */}
            {canAuthor && (
              <div
                data-testid="node-detail-sheet-actions"
                className="grid grid-cols-4 gap-1.5 border-t px-3 py-3"
                style={{ borderColor: c.rule, backgroundColor: c.bgRecess }}
              >
                {(
                  [
                    { glyph: "◐", label: "Change state", onClick: () => setTab("details") },
                    { glyph: "+", label: "Add sub-task", onClick: () => props.onAddSubtask() },
                    { glyph: "⇄", label: "Assign", onClick: () => setTab("details") },
                    { glyph: "⋯", label: "History", onClick: () => setTab("history") },
                  ] as const
                ).map((action) => (
                  <button
                    key={action.label}
                    type="button"
                    aria-label={action.label}
                    onClick={action.onClick}
                    className="flex min-h-tap items-center justify-center rounded-md border py-2 text-base transition-colors"
                    style={{
                      borderColor: c.rule2,
                      color: c.ink,
                      fontFamily: theme.fonts.mono,
                      backgroundColor: c.cardBg,
                    }}
                  >
                    {action.glyph}
                  </button>
                ))}
              </div>
            )}
          </div>
        </BottomSheet>
        {sharedOverlays}
      </>
    );
  }

  // ── Default (desktop / legacy): the right-side drawer + scrim, byte-unchanged.
  return (
    <>
      {/* Scrim over the live, dimmed editor (click — or Esc — dismisses). A
          real <button> keeps it natively keyboard-accessible. The wireframe
          dims the canvas with a warm-ink wash (`rgba(26,22,18,0.20)`); editorial
          uses the ink token at that opacity, legacy keeps the soft-mint canvas
          scrim exactly as today. */}
      <button
        type="button"
        data-testid="drawer-scrim"
        aria-label="Close details"
        onClick={onClose}
        className="absolute inset-0 z-40 cursor-default transition-opacity"
        style={{
          backgroundColor: editorial ? c.ink : PALETTE.canvas,
          opacity: editorial ? 0.2 : 0.45,
        }}
      />

      {/* The drawer panel. */}
      <aside
        data-testid="node-detail-drawer"
        data-node-id={node.id}
        className="absolute inset-y-0 right-0 z-50 flex w-[460px] max-w-full flex-col shadow-2xl"
        style={{
          backgroundColor: editorial ? c.cardBg : "#FFFFFF",
          borderLeft: `1px solid ${editorial ? c.rule : PALETTE.softBorder}`,
          animation: "drawerIn 180ms ease-out",
          fontFamily: editorial ? theme.fonts.sans : undefined,
        }}
        aria-label={`${node.title} details`}
      >
        {drawerInner}
      </aside>

      {sharedOverlays}
    </>
  );
}

// ── Permission cue (§A5, made apparent — #1043 item 4) ─────────────────────
//
// A calm, factual sentence for the drawer footer that EXPLAINS the existing
// assignee-based authorship model — never changes it. For an author it names
// the reason they can edit; for a read-only viewer it states who can edit and
// the gentle path to help ("ask to be assigned"). Returns null only when there
// is nothing honest to say (an author with no reason supplied → the caller
// still shows the prior generic line via the fallback below). Worker-friendly +
// trustable: no blame, no tier language, no per-person tally.
function permissionCueText(canAuthor: boolean, reason: AuthorReason | undefined): string | null {
  if (canAuthor) {
    switch (reason) {
      case "lead":
        return "You can edit this — you lead this team.";
      case "direct-assignee":
        return "You can edit this — you're assigned to it.";
      case "ancestor-assignee":
        return "You can edit this — you're assigned to a task above this one.";
      default:
        // An author with no reason passed (e.g. the lead editor pre-#1043 or a
        // test) keeps the prior generic reassurance, byte-unchanged.
        return "you own this node — you can grow & update it";
    }
  }
  // Read-only: explain who CAN edit + the path in, instead of a silent wall.
  return "You can view this. Editing is open to the lead and anyone assigned to this task (or a task above it). Ask to be assigned to help with it.";
}

// ── Header ────────────────────────────────────────────────────────────────

function DrawerHeader({
  title,
  state,
  canAuthor,
  onClose,
  onRename,
  theme,
}: {
  title: string;
  state: TaskState;
  canAuthor: boolean;
  onClose: () => void;
  onRename: (title: string) => void;
  theme: MapTheme;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);
  const c = theme.palette;
  const editorial = theme.mode === "editorial";

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commit = () => {
    const next = draft.trim();
    if (next && next !== title) onRename(next);
    setEditing(false);
  };

  // Editorial head (`.t-drawer .head`): a hairline-ruled block with a mono
  // kicker over a serif-display node name. Legacy keeps the plain bold title
  // row exactly as today. The title stays the same `drawer-title` element
  // (rename button for an author, plain span otherwise) under both themes — the
  // editorial fork only restyles + wraps it; structure + testids are unchanged.
  const titleClass = editorial
    ? "min-w-0 flex-1 truncate text-left"
    : "min-w-0 flex-1 truncate text-left text-lg font-bold";
  const titleStyle = editorial
    ? {
        color: c.ink,
        fontFamily: theme.fonts.serif,
        fontSize: "22px",
        letterSpacing: "-0.01em",
        lineHeight: 1.1,
      }
    : { color: PALETTE.ink };

  const titleEl =
    editing && canAuthor ? (
      // Round-4 item ③ (#1043): the rename input now LOOKS like the rendered
      // title — same className + font style, transparent background, no border
      // / padding / outline — so editing feels like typing more letters into
      // the existing words (no shift in size, weight, or vertical position).
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") {
            setDraft(title);
            setEditing(false);
          }
        }}
        data-testid="drawer-title-input"
        className={titleClass}
        style={{
          ...titleStyle,
          backgroundColor: "transparent",
          border: 0,
          outline: 0,
          padding: 0,
          margin: 0,
          appearance: "none" as const,
        }}
      />
    ) : canAuthor ? (
      <button
        type="button"
        data-testid="drawer-title"
        onClick={() => {
          setDraft(title);
          setEditing(true);
        }}
        title="Rename"
        className={titleClass}
        style={titleStyle}
      >
        {title}
      </button>
    ) : (
      // Read-only: a non-author sees the title plainly (not a rename button).
      <span
        data-testid="drawer-title"
        className={
          editorial ? "min-w-0 flex-1 truncate" : "min-w-0 flex-1 truncate text-lg font-bold"
        }
        style={titleStyle}
      >
        {title}
      </span>
    );

  if (editorial) {
    return (
      <div
        className="border-b px-5 pt-4 pb-3"
        style={{ borderColor: c.rule }}
        data-testid="drawer-head"
      >
        {/* Mono kicker — the editorial `<Label>` over the masthead title. A
            calm, factual frame for what the panel is (the work item), never a
            person (§A9). */}
        <div
          className="mb-1 text-[10px] font-semibold uppercase"
          style={{ color: c.muted, fontFamily: theme.fonts.mono, letterSpacing: "0.08em" }}
        >
          {theme.stateStyle(state).label} · task
        </div>
        <div className="flex items-start justify-between gap-3">
          {titleEl}
          <button
            type="button"
            data-testid="drawer-close"
            aria-label="Close"
            onClick={onClose}
            className="shrink-0 rounded p-1 text-base leading-none transition-colors hover:bg-black/5"
            style={{ color: c.muted }}
          >
            ✕
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start justify-between gap-3 px-5 pt-5">
      {titleEl}
      <button
        type="button"
        data-testid="drawer-close"
        aria-label="Close"
        onClick={onClose}
        className="shrink-0 rounded p-1 text-base leading-none transition-colors hover:bg-slate-100"
        style={{ color: PALETTE.muted }}
      >
        ✕
      </button>
    </div>
  );
}

// ── State chip + change menu ────────────────────────────────────────────────

function StateRow({
  state,
  canAuthor,
  onSetState,
  onSaveReason,
  theme,
}: {
  state: TaskState;
  canAuthor: boolean;
  onSetState: (state: TaskState) => void;
  /** Persist a Blocked reason as a note (#767) — undefined when notes are off. */
  onSaveReason?: ((reason: string) => void) | undefined;
  theme: MapTheme;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  return (
    <div className="flex flex-col gap-1.5 px-5 pt-3 pb-3">
      <div className="flex items-center gap-2">
        <StatusChip state={state} />
        {/* Only an author can change state — a non-author sees the chip alone. */}
        {canAuthor && (
          <div className="relative">
            <button
              type="button"
              data-testid="drawer-change-state"
              onClick={() => setMenuOpen((v) => !v)}
              className={`rounded-md border px-2.5 py-1 text-[11px] transition-colors ${
                editorial ? "hover:bg-black/5" : "hover:bg-slate-50"
              }`}
              style={{
                borderColor: editorial ? c.rule2 : PALETTE.cardBorder,
                color: editorial ? c.slate : PALETTE.slate,
              }}
            >
              ✦ change ▾
            </button>
            {menuOpen && (
              <ChangeStateMenu
                current={state}
                onSetState={onSetState}
                onClose={() => setMenuOpen(false)}
                {...(onSaveReason ? { onSaveReason } : {})}
              />
            )}
          </div>
        )}
      </div>
      {/* #953 — a calm, factual "you own this" hint right by the state control,
          so an owner sees plainly that moving the state is theirs to do. Shown
          ONLY on an owned node (the `canAuthor` gate, §A5); a read-only viewer
          never sees it. Factual, never evaluative (§A9 firewall). */}
      {canAuthor && (
        <div
          data-testid="drawer-owner-state-hint"
          className="text-[11px]"
          style={{ color: editorial ? c.faint : PALETTE.faint }}
        >
          You own this — move its state as the work moves.
        </div>
      )}
    </div>
  );
}

// ── Tabs ────────────────────────────────────────────────────────────────────

function TabButton({
  id,
  active,
  onSelect,
  children,
  theme,
}: {
  id: DrawerTab;
  active: DrawerTab;
  onSelect: (id: DrawerTab) => void;
  children: React.ReactNode;
  theme: MapTheme;
}) {
  const on = id === active;
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  // Editorial tabs (`.t-drawer .tabs button`): a flat underline bar — sans 12px,
  // the active tab in ink with a 1.5px ink bottom-border, inactive muted. Legacy
  // keeps the shipped rounded "chip" tab exactly as today.
  if (editorial) {
    return (
      <button
        type="button"
        role="tab"
        aria-selected={on}
        data-testid={`drawer-tab-${id}`}
        onClick={() => onSelect(id)}
        className="mr-[18px] -mb-px py-2.5 text-[12px] transition-colors"
        style={{
          fontFamily: theme.fonts.sans,
          letterSpacing: "0.02em",
          color: on ? c.ink : c.muted,
          fontWeight: on ? 500 : 400,
          borderBottom: `1.5px solid ${on ? c.ink : "transparent"}`,
        }}
      >
        {children}
      </button>
    );
  }
  return (
    <button
      type="button"
      role="tab"
      aria-selected={on}
      data-testid={`drawer-tab-${id}`}
      onClick={() => onSelect(id)}
      className="rounded-t-md px-3 py-1.5 text-xs font-semibold transition-colors"
      style={{
        backgroundColor: on ? PALETTE.rollupBg : "transparent",
        color: on ? PALETTE.rollupInk : PALETTE.muted,
      }}
    >
      {children}
    </button>
  );
}

// ── Details tab ──────────────────────────────────────────────────────────────

function DetailsTab({
  node,
  parent,
  members,
  blocked,
  canAuthor,
  notesEnabled,
  theme,
  onShowNotes,
  dependencies,
  allNodes,
  onSetDescription,
  onSetTargetDate,
  onSetTiming,
  estimatesEnabled,
  onSetEstimate,
  onAddSubtask,
  onAssignToggle,
  onOpenNode,
  onAddDependency,
  onRemoveDependency,
  onArchive,
}: {
  node: TreeNode;
  parent: TreeNode | null;
  members: TaskMember[];
  blocked: boolean;
  canAuthor: boolean;
  /** True when the `task_notes` layer is on — drives the blocked banner copy. */
  notesEnabled: boolean;
  theme: MapTheme;
  /** Jump to the Notes tab (the blocked banner's "add a reason" link). */
  onShowNotes: () => void;
  dependencies: readonly TaskDependency[] | undefined;
  allNodes: readonly { id: string; title: string; state: TaskState }[] | undefined;
  onSetDescription: (description: string) => void;
  onSetTargetDate: ((date: string | null) => void) | undefined;
  onSetTiming: TimingPatchHandler | undefined;
  /** True when the `task_estimates` layer is on — turns the Estimate row into a picker. */
  estimatesEnabled: boolean;
  onSetEstimate: ((minutes: number | null) => void) | undefined;
  onAddSubtask: () => void;
  onAssignToggle: (membershipId: string, assign: boolean) => void;
  onOpenNode: (id: string) => void;
  onAddDependency: ((fromNodeId: string, toNodeId: string) => void) | undefined;
  onRemoveDependency: ((dependencyId: string) => void) | undefined;
  onArchive: ((cascade?: boolean) => void) | undefined;
}) {
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  const assignees = node.assigneeIds
    .map((id) => members.find((m) => m.membershipId === id))
    .filter((m): m is TaskMember => Boolean(m));

  // The Structure & connections group counts what it holds, so its calm
  // summary line can name it ("3 sub-tasks · 1 link") without ever counting a
  // person — it's a count of the work's shape, not a tally about anyone (§A9).
  const subtaskCount = node.children.length;
  const dependencyCount =
    dependencies && allNodes
      ? comesAfter(dependencies, node.id).length + blocks(dependencies, node.id).length
      : 0;

  return (
    <div className="flex flex-col gap-1">
      {/* When blocked, a calm amber banner up top (B8.2 treatment, in-drawer). */}
      {blocked && (
        <BlockedBanner notesEnabled={notesEnabled} onShowNotes={onShowNotes} theme={theme} />
      )}

      {/* ── FOCAL SUMMARY (#930) ─────────────────────────────────────────────
          Every surface anchors on ONE focal element. Title + state already lead
          (the header + the state chip above the tabs); inside Details the focal
          info is WHO it's on. The assignee summary sits first, large and calm,
          so the tab opens on a clear "this task, on these people" rather than a
          flat dense form. Below it the description; the secondary STRUCTURE
          (parent, dependency edges, sub-tasks) is grouped lower down. */}
      <DetailRow label="Assigned to" theme={theme}>
        <AssignedToControl
          node={node}
          members={members}
          assignees={assignees}
          canAuthor={canAuthor}
          onAssignToggle={onAssignToggle}
          theme={theme}
        />
      </DetailRow>

      {/* Description — editable for an author, read-only otherwise. */}
      <div className="mt-3">
        <SectionLabel theme={theme}>Description</SectionLabel>
        {canAuthor ? (
          <DescriptionEditor
            value={node.description ?? ""}
            onCommit={onSetDescription}
            theme={theme}
          />
        ) : (
          <p
            data-testid="drawer-description-readonly"
            className="mb-2 whitespace-pre-wrap text-sm"
            style={{
              color: node.description
                ? editorial
                  ? c.ink
                  : PALETTE.slate
                : editorial
                  ? c.faint
                  : "#9CA3AF",
            }}
          >
            {node.description ?? "No description."}
          </p>
        )}
      </div>

      {/* Target date — low-key, optional metadata (§A1.2). A CORE capability:
          in EDITORIAL mode an author gets a calm calendar PICKER to set/clear it
          (B2.11 wireframe `V_B211`); LEGACY (flag off) keeps the shipped
          display-only value, byte-identical. Calm + factual, never a deadline
          tally (§A9 / brand firewall). */}
      <DetailRow label="Target date" theme={theme}>
        {editorial && canAuthor && onSetTargetDate ? (
          <TargetDatePicker value={node.targetDate} onCommit={onSetTargetDate} theme={theme} />
        ) : (
          <OptionalValue
            value={formatDate(node.targetDate)}
            testId="drawer-target-date"
            theme={theme}
          />
        )}
      </DetailRow>
      {node.needsHandoffAt ? (
        <p data-testid="drawer-needs-handoff" className="mb-2 text-sm">
          Needs a new holder
        </p>
      ) : null}
      <TaskTimingRow
        key={node.id}
        node={node}
        canEdit={canAuthor && Boolean(onSetTiming)}
        onSet={onSetTiming}
      />
      {/* Estimate — coarse, optional, TOGGLED (`task_estimates`, B2.12). When the
          layer is ON (and editorial + author) the row becomes a coarse-bucket
          PICKER (½d…2w + custom days, wireframe `V_B212`); a parent ALSO shows
          the read-time sum-of-leaves roll-up below. When OFF — or legacy, or a
          non-author — the row stays display-only and byte-unchanged. Coarse,
          never a contract / a per-person commitment / a deadline (§A9). */}
      {estimatesEnabled && editorial && canAuthor && onSetEstimate ? (
        <EstimateSection node={node} onSetEstimate={onSetEstimate} theme={theme} />
      ) : (
        <DetailRow label="Estimate" theme={theme}>
          <OptionalValue
            value={formatEstimate(node.estimateMinutes)}
            testId="drawer-estimate"
            theme={theme}
          />
        </DetailRow>
      )}

      {/* ── STRUCTURE & CONNECTIONS (#930) ───────────────────────────────────
          The task's shape — where it sits in the tree (Part of), the DAG edges
          (Comes after / Blocks), and its sub-tasks — gathered into ONE clearly
          labelled, collapsible group so Details leads with the focal summary
          rather than a flat run of rows. Defaults OPEN: nothing is hidden by
          surprise, faithful to B2.5 where every row is visible; the collapse is
          there for a deep node, not to bury capability. EVERY control inside is
          unchanged — same testids, same behaviour. */}
      <CollapsibleGroup
        label="Structure & connections"
        testId="drawer-structure-group"
        summary={describeStructure(subtaskCount, dependencyCount)}
        defaultOpen
        theme={theme}
      >
        {/* Part of (the parent — decomposition, derived from the tree). The row
            carries a LIGHT re-parent cue (#896): re-parenting already works (drag
            a card onto another on the canvas; Tab / Shift-Tab in the outline) —
            this only points at it, it doesn't rebuild it. The cue shows only for
            an author, who can actually move the node (§A5). */}
        {parent && (
          <DetailRow label="Part of" theme={theme}>
            <div className="flex flex-col items-start gap-0.5">
              <button
                type="button"
                data-testid="drawer-part-of"
                onClick={() => onOpenNode(parent.id)}
                title={
                  canAuthor
                    ? "To move this task, drag its card onto another on the map (or use Tab / Shift-Tab in the outline)."
                    : undefined
                }
                className="flex items-center gap-2 text-left text-sm transition-opacity hover:opacity-80"
                style={{ color: editorial ? c.ink : PALETTE.ink }}
              >
                <StatusDot state={parent.state} />
                {parent.title}
              </button>
              {canAuthor && (
                <span
                  data-testid="drawer-reparent-hint"
                  className="text-[11px]"
                  style={{ color: editorial ? c.faint : PALETTE.faint }}
                >
                  drag the card on the map to move it
                </span>
              )}
            </div>
          </DetailRow>
        )}

        {/* Comes after / Blocks (the dependency layer — #766, gated upstream).
            A SEPARATE concern from "Part of" (decomposition): these are the DAG
            edges that may cross branches (§A5). Whole-team visible (§A8); only
            the add/remove controls are gated on authorship. Copy is factual,
            about the work's order — never about people (§A9). */}
        {dependencies && allNodes && (
          <DependencyRows
            node={node}
            dependencies={dependencies}
            allNodes={allNodes}
            canAuthor={canAuthor}
            theme={theme}
            onOpenNode={onOpenNode}
            onAddDependency={onAddDependency}
            onRemoveDependency={onRemoveDependency}
          />
        )}

        {/* Sub-tasks. EDITORIAL (B2.5 wireframe): each child is its own
            `ChildCard` — a small card with the child's state chip, title,
            assignees, target, dependency edges, a "N sub-tasks · M notes" HAS
            line, and (if it has its own children) a nested SUB-TASK preview of
            the first one — followed by a calm roll-up line that explains the
            parent's computed state from its children ("1 of 2 sub-tasks done · 0
            blocked · stays in progress until all done"). It's a richer DISPLAY of
            data the drawer already holds (no new fetch). LEGACY keeps the shipped
            single-line row list, byte-identical. Both share the same
            `drawer-subtasks` container, `drawer-subtask-row` rows, `+ sub-task`
            (`drawer-add-subtask`), and the no-subtasks empty line. */}
        <div className="mt-4">
          {editorial ? (
            <div className="flex items-center justify-between">
              <SectionLabel theme={theme}>
                Sub-tasks{subtaskCount > 0 ? ` · ${subtaskCount}` : ""}
              </SectionLabel>
            </div>
          ) : (
            <SectionLabel theme={theme}>Sub-tasks</SectionLabel>
          )}
          {editorial ? (
            <div className="mt-2 flex flex-col" data-testid="drawer-subtasks">
              {node.children.map((child) => (
                <ChildCard
                  key={child.id}
                  child={child}
                  members={members}
                  dependencies={dependencies}
                  allNodes={allNodes}
                  theme={theme}
                  onOpenNode={onOpenNode}
                />
              ))}
              {node.children.length === 0 && (
                <div className="px-1 py-1 text-xs" style={{ color: c.faint }}>
                  No sub-tasks yet.
                </div>
              )}
              {/* Roll-up — the parent's computed state explained from its
                  children. About the WORK (counts of sub-tasks), never a tally
                  about a person (§A9 / brand firewall). Shown only when there are
                  children to roll up. */}
              {node.children.length > 0 && <RollupExplain node={node} theme={theme} />}
            </div>
          ) : (
            <ul className="mt-1 flex flex-col gap-1" data-testid="drawer-subtasks">
              {node.children.map((child) => {
                const childAssignees = child.assigneeIds
                  .map((id) => members.find((m) => m.membershipId === id))
                  .filter((m): m is TaskMember => Boolean(m));
                return (
                  <li key={child.id}>
                    <button
                      type="button"
                      data-testid="drawer-subtask-row"
                      data-node-id={child.id}
                      onClick={() => onOpenNode(child.id)}
                      className="flex w-full items-center justify-between gap-2 rounded-md px-1 py-1.5 text-left text-sm transition-colors hover:bg-slate-50"
                      style={{ color: PALETTE.ink }}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <StatusDot state={child.state} />
                        <span className="truncate">{child.title}</span>
                      </span>
                      <AvatarRow members={childAssignees} size={20} />
                    </button>
                  </li>
                );
              })}
              {node.children.length === 0 && (
                <li className="px-1 py-1 text-xs" style={{ color: PALETTE.faint }}>
                  No sub-tasks yet.
                </li>
              )}
            </ul>
          )}
          {/* Only an author can grow the subtree (§A5 recursive ownership). */}
          {canAuthor && (
            <button
              type="button"
              data-testid="drawer-add-subtask"
              onClick={onAddSubtask}
              className="mt-1 px-1 text-sm transition-opacity hover:opacity-80"
              style={{ color: editorial ? c.teal : PALETTE.teal }}
            >
              + sub-task
            </button>
          )}
        </div>
      </CollapsibleGroup>

      {/* Remove from map (#896) — the one obvious place to take a task off the
          map. It ARCHIVES (recoverable): history + provenance are preserved,
          never a hard delete (§A6). Only an author may remove (§A5). A small
          confirm step keeps it deliberate without alarm — calm, factual copy. */}
      {canAuthor && onArchive && <RemoveFromMap node={node} onArchive={onArchive} theme={theme} />}
    </div>
  );
}

// ── Editorial child card (B2.5 wireframe `ChildCard`) ────────────────────────

/**
 * The editorial sub-task card. A richer, calmer DISPLAY of one child task —
 * shown inside the parent's Details tab so the whole subtree is legible without
 * leaving context (the wireframe's `ChildCard`). Renders only what the drawer
 * already holds on the live `TreeNode`: state chip, title, assignees, target
 * date, its dependency edges (when the layer is on), a "N sub-tasks" HAS line,
 * and a nested SUB-TASK preview of its first child. No new fetch; no per-person
 * number (§A9). Editorial-only — the legacy path keeps the one-line row.
 *
 * The whole card is the clickable row (preserves the `drawer-subtask-row`
 * open-on-click behaviour); the nested sub-task preview stops propagation so it
 * can carry its own (currently inert) presentation without hijacking the click.
 */
function ChildCard({
  child,
  members,
  dependencies,
  allNodes,
  theme,
  onOpenNode,
}: {
  child: TreeNode;
  members: TaskMember[];
  dependencies: readonly TaskDependency[] | undefined;
  allNodes: readonly { id: string; title: string; state: TaskState }[] | undefined;
  theme: MapTheme;
  onOpenNode: (id: string) => void;
}) {
  const c = theme.palette;
  const childAssignees = child.assigneeIds
    .map((id) => members.find((m) => m.membershipId === id))
    .filter((m): m is TaskMember => Boolean(m));

  // The child's own dependency edges (when the layer is on) — named via the
  // team node list. Factual order of the work, never about people (§A9).
  const nodeById = allNodes ? new Map(allNodes.map((n) => [n.id, n])) : null;
  const after = dependencies ? comesAfter(dependencies, child.id) : [];
  const blocking = dependencies ? blocks(dependencies, child.id) : [];
  const depRows: { kind: string; title: string }[] = [];
  if (nodeById) {
    for (const e of after) {
      depRows.push({ kind: "AFTER", title: nodeById.get(e.from_node_id)?.title ?? "Another task" });
    }
    for (const e of blocking) {
      depRows.push({ kind: "BLOCKS", title: nodeById.get(e.to_node_id)?.title ?? "Another task" });
    }
  }

  const subCount = child.children.length;
  const firstSub = child.children[0];
  const target = formatDate(child.targetDate);

  return (
    <button
      type="button"
      data-testid="drawer-subtask-row"
      data-node-id={child.id}
      onClick={() => onOpenNode(child.id)}
      className="mb-2.5 w-full rounded-md border p-3 text-left transition-shadow hover:shadow-sm"
      style={{ borderColor: c.rule, backgroundColor: c.cardBg, color: c.ink }}
    >
      {/* Top row: state chip · title · assignees. */}
      <div className="mb-1.5 flex items-center gap-2">
        <StatusChip state={child.state} />
        <span
          className="min-w-0 flex-1 truncate text-[13.5px] font-medium"
          style={{ color: c.ink }}
        >
          {child.title}
        </span>
        {childAssignees.length > 0 && <AvatarRow members={childAssignees} size={20} />}
      </div>

      {/* k/v metadata grid — TARGET, dependency edges, HAS. Mono labels in the
          editorial `t-label` style; dependency targets in teal. */}
      {(target || depRows.length > 0 || subCount > 0) && (
        <div
          className="grid items-center gap-x-2 gap-y-1 text-[11.5px]"
          style={{ gridTemplateColumns: "60px 1fr", color: c.slate }}
        >
          {target && (
            <>
              <ChildLabel theme={theme}>TARGET</ChildLabel>
              <span style={{ fontFamily: theme.fonts.mono }}>{target}</span>
            </>
          )}
          {depRows.map((d, i) => (
            <Fragment key={`${d.kind}-${i}`}>
              <ChildLabel theme={theme}>{d.kind}</ChildLabel>
              <span style={{ color: c.teal }}>{d.title}</span>
            </Fragment>
          ))}
          {subCount > 0 && (
            <>
              <ChildLabel theme={theme}>HAS</ChildLabel>
              <span style={{ fontFamily: theme.fonts.mono, fontSize: "10.5px", color: c.muted }}>
                {subCount} sub-task{subCount === 1 ? "" : "s"}
              </span>
            </>
          )}
        </div>
      )}

      {/* Nested SUB-TASK preview — the first child, shown on a recessed strip so
          one extra level of structure reads without opening the child. Stops the
          click from bubbling to the open-this-child handler. */}
      {firstSub && (
        <div
          className="mt-2.5 border-t pt-2.5"
          style={{ borderColor: c.rule, borderStyle: "dashed" }}
        >
          <ChildLabel theme={theme}>SUB-TASK</ChildLabel>
          <div
            className="mt-1.5 flex items-center gap-2.5 rounded-md px-3 py-2"
            style={{ backgroundColor: c.bgRecess }}
          >
            <StatusChip state={firstSub.state} />
            <span className="min-w-0 flex-1 truncate text-[12.5px]" style={{ color: c.ink }}>
              {firstSub.title}
            </span>
            <AvatarRow
              members={firstSub.assigneeIds
                .map((id) => members.find((m) => m.membershipId === id))
                .filter((m): m is TaskMember => Boolean(m))}
              size={18}
            />
          </div>
        </div>
      )}
    </button>
  );
}

/** A mono uppercase key label inside a `ChildCard`'s k/v grid (editorial). */
function ChildLabel({ children, theme }: { children: React.ReactNode; theme: MapTheme }) {
  return (
    <span
      className="self-center text-[10px] font-semibold uppercase"
      style={{
        color: theme.palette.muted,
        fontFamily: theme.fonts.mono,
        letterSpacing: "0.06em",
      }}
    >
      {children}
    </span>
  );
}

/**
 * The editorial roll-up explainer. States, in calm factual copy, how the
 * parent's computed state follows from its children: "N of M sub-tasks done · K
 * blocked · stays in progress until all are done." A count of the WORK's shape,
 * never a tally about a person (§A9 / brand firewall). Display-only.
 */
function RollupExplain({ node, theme }: { node: TreeNode; theme: MapTheme }) {
  const c = theme.palette;
  // Direct children only (the level this card shows), so the count matches what
  // the user sees rendered above. `done` / `blocked` are factual states.
  const total = node.children.length;
  const done = node.children.filter((k) => k.state === "done").length;
  const blockedCount = node.children.filter((k) => k.state === "blocked").length;
  const allDone = done === total;
  return (
    <div
      data-testid="drawer-rollup-explain"
      className="mt-1 rounded-md px-3 py-2.5 text-[11.5px]"
      style={{ backgroundColor: c.bgRecess, color: c.muted }}
    >
      <strong style={{ color: c.slate }}>Roll-up:</strong> {done} of {total} sub-task
      {total === 1 ? "" : "s"} done · {blockedCount} blocked · this parent stays{" "}
      <strong style={{ color: c.slate }}>
        {allDone
          ? theme.stateStyle("done").label.toLowerCase()
          : theme.stateStyle(node.state).label.toLowerCase()}
      </strong>{" "}
      until all are done.
    </div>
  );
}

// ── Remove from map (B2.5 — #896) ────────────────────────────────────────────

/**
 * The "Remove from map" action. A two-step confirm (the calm pattern: a quiet
 * link that reveals a factual confirm row), so removal is deliberate but never
 * loud. Removal is a soft archive — recoverable, provenance kept (§A6); the
 * copy says so plainly rather than warning.
 *
 * Branch removal (#1043 — resolving the #1087 follow-up): the backend now offers
 * a `cascade` flag (`archive_subtree`). A LEAF removes the one node (no cascade),
 * exactly as before. A PARENT removes the whole live branch in one recoverable
 * action — "Remove this and its N subtasks" → `onArchive(true)`. This replaces
 * the prior "remove its N sub-tasks first" block (which existed only because a
 * single-node archive would re-root the children); the cascade keeps the branch
 * together, so children are never orphaned. Either way it is a SOFT archive:
 * recoverable, history kept — not a hard delete.
 */
/**
 * Round-4 item ⑤.a (#1043) — when the lead is about to remove a PARENT, list
 * the affected subtasks concretely so the action is never a surprise. Names a
 * few of the immediate (live) children (up to `PREVIEW_LIMIT`); if there are
 * more, adds a calm "…and N more" line. Counts WORK never people (§A9): this
 * is a preview of TASKS about to move, not a list of people.
 */
const PARENT_REMOVE_PREVIEW_LIMIT = 4;

function ParentRemovePreview({
  node,
  editorial,
  theme,
}: {
  node: TreeNode;
  editorial: boolean;
  theme: MapTheme;
}) {
  const c = theme.palette;
  const total = node.totalDescendants;
  // `node.children` is already the live (non-archived) slice — the default tree
  // GET excludes archived rows, so this is a list of what's about to move.
  const preview = node.children.slice(0, PARENT_REMOVE_PREVIEW_LIMIT);
  const overflow = node.children.length - preview.length;
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-[13px]" style={{ color: editorial ? c.slate : PALETTE.slate }}>
        Remove “{node.title}” and its {total} subtask{total === 1 ? "" : "s"}? They move to the
        archive — recoverable, history kept.
      </p>
      {preview.length > 0 && (
        <ul
          data-testid="drawer-remove-preview"
          className="ml-1 list-disc pl-4 text-[12px]"
          style={{ color: editorial ? c.muted : PALETTE.muted }}
        >
          {preview.map((child) => (
            <li key={child.id} className="truncate">
              {child.title}
            </li>
          ))}
          {overflow > 0 && (
            <li className="list-none italic" style={{ color: editorial ? c.faint : PALETTE.muted }}>
              …and {overflow} more
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function RemoveFromMap({
  node,
  onArchive,
  theme,
}: {
  node: TreeNode;
  onArchive: (cascade?: boolean) => void;
  theme: MapTheme;
}) {
  const [confirming, setConfirming] = useState(false);
  const hasChildren = node.children.length > 0;
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  const blockedStyle = theme.stateStyle("blocked");
  const count = node.totalDescendants;

  return (
    <div
      className="mt-6 border-t pt-4"
      style={{ borderColor: editorial ? c.rule : "#F3F4F6" }}
      data-testid="drawer-remove"
    >
      {confirming ? (
        <div className="flex flex-col gap-3">
          {hasChildren ? (
            <ParentRemovePreview node={node} editorial={editorial} theme={theme} />
          ) : (
            <p className="text-[13px]" style={{ color: editorial ? c.slate : PALETTE.slate }}>
              Remove “{node.title}” from the map? You can bring it back later — its history is kept.
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="drawer-remove-confirm"
              // A leaf removes the one node; a parent cascades the whole branch.
              onClick={() => onArchive(hasChildren)}
              className="rounded-md border px-3 py-1.5 text-[12px] font-semibold transition-colors hover:bg-amber-50"
              style={
                editorial
                  ? { borderColor: blockedStyle.dot ?? blockedStyle.fg, color: blockedStyle.fg }
                  : { borderColor: "#D97706", color: "#92400E" }
              }
            >
              {hasChildren
                ? `Remove this and its ${count} subtask${count === 1 ? "" : "s"}`
                : "Remove from map"}
            </button>
            {/* Round-4 item ⑤.a (#1043): the second, explicit cascade=false path
                — remove the parent + keep subtasks as separate outcomes. Surfaces
                the previously-silent re-root as a deliberate user choice. Only
                shown for a parent. */}
            {hasChildren && (
              <button
                type="button"
                data-testid="drawer-remove-promote"
                onClick={() => onArchive(false)}
                className={`rounded-md px-3 py-1.5 text-[12px] transition-colors ${
                  editorial ? "hover:bg-black/5" : "hover:bg-slate-50"
                }`}
                style={{
                  color: editorial ? c.slate : PALETTE.slate,
                  border: `1px dashed ${editorial ? c.rule : "#E5E7EB"}`,
                }}
                title="Remove just this node — its subtasks become separate top-level outcomes."
              >
                Or keep the {count} subtask{count === 1 ? "" : "s"} as outcomes
              </button>
            )}
            <button
              type="button"
              data-testid="drawer-remove-cancel"
              onClick={() => setConfirming(false)}
              className={`rounded-md px-3 py-1.5 text-[12px] transition-colors ${
                editorial ? "hover:bg-black/5" : "hover:bg-slate-50"
              }`}
              style={{ color: editorial ? c.muted : PALETTE.muted }}
            >
              Keep
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          data-testid="drawer-remove-open"
          onClick={() => setConfirming(true)}
          className="text-[13px] transition-opacity hover:opacity-80"
          style={{ color: editorial ? c.muted : PALETTE.muted }}
        >
          Remove from map
        </button>
      )}
    </div>
  );
}

// ── Dependency rows (B2.7 — #766) ────────────────────────────────────────────

/**
 * The "Comes after / Blocks" rows (B2.7). Direction convention (matches the
 * backend + `dependencyGraph`): `from ──▶ to` = "from comes before to".
 *   - "Comes after" = the edges where THIS node is `to` (what it waits on);
 *     adding one picks a prerequisite → `from = picked, to = this`.
 *   - "Blocks"      = the edges where THIS node is `from` (what waits on it);
 *     adding one picks a dependent → `from = this, to = picked`.
 * Each row is removable (for an author); each opens the other node on click.
 */
function DependencyRows({
  node,
  dependencies,
  allNodes,
  canAuthor,
  theme,
  onOpenNode,
  onAddDependency,
  onRemoveDependency,
}: {
  node: TreeNode;
  dependencies: readonly TaskDependency[];
  allNodes: readonly { id: string; title: string; state: TaskState }[];
  canAuthor: boolean;
  theme: MapTheme;
  onOpenNode: (id: string) => void;
  onAddDependency: ((fromNodeId: string, toNodeId: string) => void) | undefined;
  onRemoveDependency: ((dependencyId: string) => void) | undefined;
}) {
  const nodeById = useMemo(() => new Map(allNodes.map((n) => [n.id, n])), [allNodes]);
  const after = comesAfter(dependencies, node.id);
  const blocking = blocks(dependencies, node.id);

  return (
    <>
      <DependencyRow
        label="Comes after"
        testId="drawer-comes-after"
        node={node}
        edges={after}
        endpointOf={(edge) => edge.from_node_id}
        nodeById={nodeById}
        canAuthor={canAuthor}
        theme={theme}
        emptyHint="Nothing yet"
        pickerVerb="comes before this"
        // Picking X means "X comes before THIS" → from = X, to = this.
        toEdge={(pickedId) => ({ from: pickedId, to: node.id })}
        allNodes={allNodes}
        dependencies={dependencies}
        onOpenNode={onOpenNode}
        onAddDependency={onAddDependency}
        onRemoveDependency={onRemoveDependency}
      />
      <DependencyRow
        label="Blocks"
        testId="drawer-blocks"
        node={node}
        edges={blocking}
        endpointOf={(edge) => edge.to_node_id}
        nodeById={nodeById}
        canAuthor={canAuthor}
        theme={theme}
        emptyHint="Nothing yet"
        pickerVerb="comes after this"
        // Picking X means "THIS comes before X" → from = this, to = X.
        toEdge={(pickedId) => ({ from: node.id, to: pickedId })}
        allNodes={allNodes}
        dependencies={dependencies}
        onOpenNode={onOpenNode}
        onAddDependency={onAddDependency}
        onRemoveDependency={onRemoveDependency}
      />
    </>
  );
}

function DependencyRow({
  label,
  testId,
  node,
  edges,
  endpointOf,
  nodeById,
  canAuthor,
  theme,
  emptyHint,
  pickerVerb,
  toEdge,
  allNodes,
  dependencies,
  onOpenNode,
  onAddDependency,
  onRemoveDependency,
}: {
  label: string;
  testId: string;
  node: TreeNode;
  edges: TaskDependency[];
  /** Which endpoint of the edge is the OTHER node (the one to name + open). */
  endpointOf: (edge: TaskDependency) => string;
  nodeById: Map<string, { id: string; title: string; state: TaskState }>;
  canAuthor: boolean;
  theme: MapTheme;
  emptyHint: string;
  pickerVerb: string;
  /** Map a picked node id → the `{from, to}` of the edge to create. */
  toEdge: (pickedId: string) => { from: string; to: string };
  allNodes: readonly { id: string; title: string; state: TaskState }[];
  dependencies: readonly TaskDependency[];
  onOpenNode: (id: string) => void;
  onAddDependency: ((fromNodeId: string, toNodeId: string) => void) | undefined;
  onRemoveDependency: ((dependencyId: string) => void) | undefined;
}) {
  const [picking, setPicking] = useState(false);
  const pal = theme.palette;
  const editorial = theme.mode === "editorial";

  // Candidates for the picker: every OTHER node that isn't already linked in
  // this direction and whose link wouldn't close a cycle (mirrors the backend
  // 422 guard so we never offer a doomed pick).
  const alreadyLinked = new Set(edges.map((e) => endpointOf(e)));
  const candidates = allNodes.filter((other) => {
    if (other.id === node.id) return false;
    if (alreadyLinked.has(other.id)) return false;
    const { from, to } = toEdge(other.id);
    return !wouldCreateDependencyCycle(dependencies, from, to);
  });

  return (
    <DetailRow label={label} theme={theme}>
      <div className="flex flex-col items-end gap-1" data-testid={testId}>
        {edges.length === 0 && (
          <span className="text-sm" style={{ color: editorial ? pal.faint : PALETTE.faint }}>
            {emptyHint}
          </span>
        )}
        {edges.map((edge) => {
          const otherId = endpointOf(edge);
          const other = nodeById.get(otherId);
          return (
            <div key={edge.id} className="flex items-center gap-1.5" data-dep-id={edge.id}>
              <button
                type="button"
                data-testid="dependency-link"
                data-node-id={otherId}
                onClick={() => onOpenNode(otherId)}
                className="flex items-center gap-1.5 text-left text-sm transition-opacity hover:opacity-80"
                // Editorial names the dependency endpoint in teal (the wireframe's
                // dependency colour); legacy keeps the ink link colour.
                style={{ color: editorial ? pal.teal : PALETTE.ink }}
              >
                {other ? <StatusDot state={other.state} /> : null}
                <span className="truncate">{other?.title ?? "Another task"}</span>
              </button>
              {canAuthor && onRemoveDependency && (
                <button
                  type="button"
                  data-testid="dependency-remove"
                  aria-label={`Remove link to ${other?.title ?? "task"}`}
                  onClick={() => onRemoveDependency(edge.id)}
                  className={`rounded p-0.5 text-xs leading-none transition-colors ${
                    editorial ? "hover:bg-black/5" : "hover:bg-slate-100"
                  }`}
                  style={{ color: editorial ? pal.faint : PALETTE.faint }}
                >
                  ✕
                </button>
              )}
            </div>
          );
        })}
        {canAuthor && onAddDependency && (
          <div className="relative">
            <button
              type="button"
              data-testid="dependency-add"
              onClick={() => setPicking((v) => !v)}
              className={`rounded-md border px-2 py-0.5 text-[11px] transition-colors ${
                editorial ? "hover:bg-black/5" : "hover:bg-slate-50"
              }`}
              style={{
                borderColor: editorial ? pal.rule2 : PALETTE.softBorder,
                color: editorial ? pal.teal : PALETTE.teal,
              }}
            >
              + add
            </button>
            {picking && (
              <DependencyPicker
                verb={pickerVerb}
                candidates={candidates}
                theme={theme}
                onPick={(pickedId) => {
                  const { from, to } = toEdge(pickedId);
                  onAddDependency(from, to);
                  setPicking(false);
                }}
                onClose={() => setPicking(false)}
              />
            )}
          </div>
        )}
      </div>
    </DetailRow>
  );
}

/** A small dropdown listing nodes that can be linked in the current direction. */
function DependencyPicker({
  verb,
  candidates,
  theme,
  onPick,
  onClose,
}: {
  verb: string;
  candidates: readonly { id: string; title: string; state: TaskState }[];
  theme: MapTheme;
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  const pal = theme.palette;
  const editorial = theme.mode === "editorial";
  return (
    <>
      {/* Click-away scrim. */}
      <button
        type="button"
        aria-label="Close picker"
        onClick={onClose}
        className="fixed inset-0 z-30 cursor-default"
      />
      <div
        data-testid="dependency-picker"
        className="absolute right-0 top-full z-40 mt-1 max-h-64 w-64 overflow-auto rounded-lg border p-1 shadow-xl"
        style={{
          borderColor: editorial ? pal.rule : PALETTE.softBorder,
          backgroundColor: editorial ? pal.cardBg : "#FFFFFF",
        }}
      >
        <div
          className="px-2 py-1 text-[11px]"
          style={{ color: editorial ? pal.faint : PALETTE.faint }}
        >
          pick a task that {verb}
        </div>
        {candidates.length === 0 && (
          <div
            className="px-2 py-2 text-xs"
            style={{ color: editorial ? pal.faint : PALETTE.faint }}
          >
            No eligible tasks.
          </div>
        )}
        {candidates.map((cand) => (
          <button
            key={cand.id}
            type="button"
            data-testid="dependency-picker-option"
            data-node-id={cand.id}
            onClick={() => onPick(cand.id)}
            className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
              editorial ? "hover:bg-black/5" : "hover:bg-slate-50"
            }`}
            style={{ color: editorial ? pal.ink : PALETTE.ink }}
          >
            <StatusDot state={cand.state} />
            <span className="truncate">{cand.title}</span>
          </button>
        ))}
      </div>
    </>
  );
}

function BlockedBanner({
  notesEnabled,
  onShowNotes,
  theme,
}: {
  notesEnabled: boolean;
  onShowNotes: () => void;
  theme: MapTheme;
}) {
  const editorial = theme.mode === "editorial";
  // Editorial uses the theme-aware blocked chip colours (warm tint + darker
  // amber ink); legacy keeps the shipped chip bg + #92400E ink exactly.
  const blockedStyle = editorial ? theme.stateStyle("blocked") : taskStateStyle("blocked");
  const bannerInk = editorial ? blockedStyle.fg : "#92400E";
  return (
    <div
      data-testid="drawer-blocked-banner"
      className="mb-3 flex items-center gap-2 rounded-lg px-3 py-2 text-xs"
      style={{ backgroundColor: blockedStyle.chipBg, color: bannerInk }}
    >
      <span aria-hidden>💬</span>
      {notesEnabled ? (
        // With the notes layer on, a reason is a real note — point there.
        <span>
          Blocked — leave a reason in{" "}
          <button
            type="button"
            data-testid="blocked-banner-notes-link"
            onClick={onShowNotes}
            className="font-semibold underline transition-opacity hover:opacity-80"
            style={{ color: bannerInk }}
          >
            Notes
          </button>
          .
        </span>
      ) : (
        <span>Blocked — a reason note can be added in a later update.</span>
      )}
    </div>
  );
}

// ── Target-date picker (B2.11, #1043, wireframe V_B211) ──────────────────────
// A calm calendar PICKER for a node's optional target date. Editorial-only +
// author-only (the caller gates both); never renders in legacy. The set/clear
// path is a single PATCH (one `target_date_set` event). The date carried on the
// wire is an ISO `YYYY-MM-DD` string; we parse + format LOCALLY (never `new
// Date(iso)`, which would shift by the UTC offset). A node carrying a target
// reads the amber `◷` glyph in the small node-preview — amber, never red (§A9 /
// brand firewall).

/** Parse an ISO `YYYY-MM-DD` into {y,m,d} (1-based month) — local, TZ-safe. */
function parseIsoDate(iso: string | null): { y: number; m: number; d: number } | null {
  if (!iso) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  return { y, m, d };
}

/** Format {y,m,d} (1-based month) back to an ISO `YYYY-MM-DD` string. */
function toIsoDate(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** A short "Jun 15" / "Jun 15, 2026" label for the set button + trigger. */
function shortDateLabel(y: number, m: number, d: number): string {
  return formatDate(toIsoDate(y, m, d)) ?? toIsoDate(y, m, d);
}

function TargetDatePicker({
  value,
  onCommit,
  theme,
}: {
  /** Current target as an ISO `YYYY-MM-DD` string, or null when unset. */
  value: string | null;
  /** Set (ISO string) or clear (null) the target — one PATCH per call. */
  onCommit: (date: string | null) => void;
  theme: MapTheme;
}) {
  const c = theme.palette;
  const amber = theme.stateStyle("blocked").dot ?? theme.stateStyle("blocked").fg;
  const [open, setOpen] = useState(false);

  const current = parseIsoDate(value);
  const today = new Date();
  const todayParts = { y: today.getFullYear(), m: today.getMonth() + 1, d: today.getDate() };

  // The month the grid is showing — seeded from the current target (or today),
  // re-seeded whenever the picker (re)opens or the node's value changes.
  const [view, setView] = useState(() => ({
    y: current?.y ?? todayParts.y,
    m: current?.m ?? todayParts.m,
  }));
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-seed on (re)open + value change only
  useEffect(() => {
    const seed = parseIsoDate(value);
    setView({ y: seed?.y ?? todayParts.y, m: seed?.m ?? todayParts.m });
  }, [value, open]);

  // Build the month grid: leading blanks (Mon-first) then 1..daysInMonth,
  // padded to a whole number of weeks.
  const cells = useMemo(() => {
    const firstDow = new Date(view.y, view.m - 1, 1).getDay(); // 0=Sun
    const leading = (firstDow + 6) % 7; // Mon-first offset
    const daysInMonth = new Date(view.y, view.m, 0).getDate();
    const out: (number | null)[] = [];
    for (let i = 0; i < leading; i++) out.push(null);
    for (let d = 1; d <= daysInMonth; d++) out.push(d);
    while (out.length % 7 !== 0) out.push(null);
    return out;
  }, [view.y, view.m]);

  const stepMonth = (delta: number) => {
    setView((v) => {
      // Absolute month index (0-based) so a step rolls the year cleanly.
      const idx = v.y * 12 + (v.m - 1) + delta;
      return { y: Math.floor(idx / 12), m: (idx % 12) + 1 };
    });
  };

  const triggerLabel = current ? shortDateLabel(current.y, current.m, current.d) : "Set a date";

  return (
    <div className="relative">
      {/* Trigger — shows the current target with the amber ◷ glyph (or a calm
          "Set a date" affordance when unset). */}
      <button
        type="button"
        data-testid="drawer-target-date-trigger"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-left transition-colors hover:bg-black/5"
        style={{
          fontFamily: theme.fonts.mono,
          fontSize: "12.5px",
          color: current ? c.ink : c.faint,
        }}
      >
        {current && (
          <span aria-hidden style={{ color: amber }}>
            ◷
          </span>
        )}
        {triggerLabel}
      </button>

      {open && (
        <div
          data-testid="drawer-target-date-picker"
          className="absolute left-0 top-full z-30 mt-1 overflow-hidden"
          style={{
            width: 280,
            backgroundColor: c.cardBg,
            border: `1px solid ${c.rule}`,
            borderRadius: theme.radii.lg,
            boxShadow: "0 8px 28px rgba(0,0,0,0.08)",
          }}
        >
          {/* Month header with ‹ / › navigation. */}
          <div
            className="flex items-center justify-between px-3 py-2.5"
            style={{ borderBottom: `1px solid ${c.rule}` }}
          >
            <button
              type="button"
              data-testid="target-picker-prev-month"
              onClick={() => stepMonth(-1)}
              className="rounded px-2 py-0.5 transition-colors hover:bg-black/5"
              style={{ color: c.muted }}
              aria-label="Previous month"
            >
              ‹
            </button>
            <span
              data-testid="target-picker-month"
              className="uppercase"
              style={{ fontFamily: theme.fonts.mono, fontSize: "11.5px", letterSpacing: "0.08em" }}
            >
              {MONTH_NAMES[view.m - 1]} {view.y}
            </span>
            <button
              type="button"
              data-testid="target-picker-next-month"
              onClick={() => stepMonth(1)}
              className="rounded px-2 py-0.5 transition-colors hover:bg-black/5"
              style={{ color: c.muted }}
              aria-label="Next month"
            >
              ›
            </button>
          </div>

          {/* Day grid — Mon-first mono labels + tabular-nums dates. */}
          <div className="grid grid-cols-7 gap-0.5 p-2.5">
            {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: fixed 7-label header
                key={`dow-${i}`}
                className="py-1 text-center"
                style={{ fontFamily: theme.fonts.mono, fontSize: "10px", color: c.faint }}
              >
                {d}
              </div>
            ))}
            {cells.map((d, i) => {
              const isSel =
                d != null && current?.y === view.y && current?.m === view.m && current?.d === d;
              const isToday =
                d != null &&
                todayParts.y === view.y &&
                todayParts.m === view.m &&
                todayParts.d === d;
              return (
                <button
                  // biome-ignore lint/suspicious/noArrayIndexKey: positional grid cell
                  key={`cell-${i}`}
                  type="button"
                  disabled={d == null}
                  data-testid={d != null ? `target-picker-day-${d}` : undefined}
                  onClick={() => {
                    if (d == null) return;
                    onCommit(toIsoDate(view.y, view.m, d));
                    setOpen(false);
                  }}
                  className="py-1.5 text-center transition-colors"
                  style={{
                    fontFamily: theme.fonts.mono,
                    fontSize: "12.5px",
                    fontVariantNumeric: "tabular-nums",
                    borderRadius: theme.radii.md,
                    cursor: d == null ? "default" : "pointer",
                    fontWeight: isSel ? 600 : 400,
                    backgroundColor: isSel ? c.ink : isToday ? c.bgRecess : "transparent",
                    color: isSel ? c.cardBg : d == null ? "transparent" : c.ink,
                  }}
                >
                  {d ?? "·"}
                </button>
              );
            })}
          </div>

          {/* Footer — clear (teal link) · cancel (ghost) · set (ink button). */}
          <div
            className="flex items-center justify-between px-3 py-2.5"
            style={{ borderTop: `1px solid ${c.rule}` }}
          >
            <button
              type="button"
              data-testid="target-picker-clear"
              onClick={() => {
                onCommit(null);
                setOpen(false);
              }}
              className="transition-opacity hover:opacity-80"
              style={{ fontSize: "11.5px", color: c.teal }}
            >
              clear
            </button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="target-picker-cancel"
                onClick={() => setOpen(false)}
                className="rounded-md border px-2.5 py-1 text-[11.5px] transition-colors hover:bg-black/5"
                style={{ borderColor: c.rule2, color: c.slate }}
              >
                cancel
              </button>
              <button
                type="button"
                data-testid="target-picker-set"
                disabled={!current}
                onClick={() => {
                  if (current) {
                    onCommit(toIsoDate(current.y, current.m, current.d));
                    setOpen(false);
                  }
                }}
                className="rounded-md px-2.5 py-1 text-[11.5px] font-semibold transition-opacity hover:opacity-90 disabled:opacity-40"
                style={{ backgroundColor: c.ink, color: c.cardBg }}
              >
                {current ? `set ${shortDateLabel(current.y, current.m, current.d)}` : "set"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DescriptionEditor({
  value,
  onCommit,
  theme,
}: {
  value: string;
  onCommit: (next: string) => void;
  theme: MapTheme;
}) {
  const [draft, setDraft] = useState(value);
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  // Keep the local draft in step when the live node changes (e.g. reopened on
  // a different node) — the editor is the single source of truth.
  useEffect(() => {
    setDraft(value);
  }, [value]);

  const commit = () => {
    if (draft !== value) onCommit(draft);
  };

  return (
    <textarea
      data-testid="drawer-description"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      placeholder="Add a short description…"
      rows={3}
      className="mb-2 w-full resize-none rounded-lg border p-2.5 text-sm outline-none"
      style={
        editorial
          ? {
              borderColor: c.rule,
              backgroundColor: c.bgRecess,
              color: c.ink,
              fontFamily: theme.fonts.sans,
            }
          : { borderColor: PALETTE.cardBorder, backgroundColor: "#F7F8FA", color: PALETTE.slate }
      }
    />
  );
}

function AssignedToControl({
  node,
  members,
  assignees,
  canAuthor,
  onAssignToggle,
  theme,
}: {
  node: TreeNode;
  members: TaskMember[];
  assignees: TaskMember[];
  canAuthor: boolean;
  onAssignToggle: (membershipId: string, assign: boolean) => void;
  theme: MapTheme;
}) {
  const [open, setOpen] = useState(false);
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  return (
    <div className="relative flex items-center gap-2">
      <AvatarRow members={assignees} size={26} />
      {assignees.length === 0 && (
        <span className="text-sm" style={{ color: editorial ? c.faint : PALETTE.faint }}>
          No one yet
        </span>
      )}
      {/* Only an author may change who's assigned within the subtree (§A5). */}
      {canAuthor && (
        <button
          type="button"
          data-testid="drawer-assign-add"
          onClick={() => setOpen((v) => !v)}
          className={`rounded-md border px-2.5 py-1 text-[11px] transition-colors ${
            editorial ? "hover:bg-black/5" : "hover:bg-slate-50"
          }`}
          style={{
            borderColor: editorial ? c.rule2 : PALETTE.softBorder,
            color: editorial ? c.teal : PALETTE.teal,
          }}
        >
          + add
        </button>
      )}
      {open && canAuthor && (
        <div className="absolute right-0 top-full z-30 mt-1">
          <AssignPopover
            nodeTitle={node.title}
            members={members}
            assignedIds={node.assigneeIds}
            onToggle={onAssignToggle}
            onClose={() => setOpen(false)}
            variant="panel"
          />
        </div>
      )}
    </div>
  );
}

// The Notes tab is the real `<NotesTab/>` (B4.1, #767) and exists only when
// `task_notes` is on for the team — there is no gated placeholder body (#930):
// an absent layer reads as "later" via the team's optional features, never a
// tab that does nothing. The #760 placeholder was retired with the tab-coherence
// pass.

// The History tab is now the real `<NodeHistoryView/>` (B3.1 + the B3.2
// timeline strip), fed by `useNodeHistory` above — issue #763 filled the seam
// the #760 placeholder left.

// ── Structure & connections group (#930) ─────────────────────────────────────

/**
 * A clearly-labelled, collapsible group used to gather the task's secondary
 * STRUCTURE (parent, dependency edges, sub-tasks) below the focal summary, so
 * the Details tab anchors on ONE focal element rather than reading as a flat
 * dense form. Defaults open (`defaultOpen`) so nothing is hidden by surprise —
 * the collapse is a calm convenience for a deep node, not a way to bury
 * capability. The header is a real <button> (keyboard-accessible, `aria-expanded`)
 * with a quiet, optional one-line summary of what it holds (about the work's
 * shape, never a person — §A9).
 */
function CollapsibleGroup({
  label,
  testId,
  summary,
  defaultOpen = true,
  children,
  theme,
}: {
  label: string;
  testId: string;
  /** A quiet one-line summary of contents, shown when collapsed (optional). */
  summary?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  theme: MapTheme;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  return (
    <div className="mt-4" data-testid={testId} data-open={open ? "true" : "false"}>
      <button
        type="button"
        data-testid={`${testId}-toggle`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 py-1 text-left"
      >
        <span
          className="text-[10px] transition-transform"
          aria-hidden
          style={{
            color: editorial ? c.muted : PALETTE.muted,
            transform: open ? "rotate(90deg)" : "none",
          }}
        >
          ▶
        </span>
        <span
          className="text-[11px] font-semibold uppercase tracking-wide"
          style={
            editorial
              ? { color: c.muted, letterSpacing: "0.06em", fontFamily: theme.fonts.mono }
              : { color: "#6B7280", letterSpacing: "0.5px" }
          }
        >
          {label}
        </span>
        {summary && (
          <span
            className="ml-auto text-[11px]"
            style={{ color: editorial ? c.faint : PALETTE.faint }}
          >
            {summary}
          </span>
        )}
      </button>
      {open && <div className="mt-1">{children}</div>}
    </div>
  );
}

/**
 * The Structure & connections summary line — a count of the work's SHAPE
 * (sub-tasks + dependency links), never a tally about a person (§A9). Returns
 * an empty string when there is nothing to summarise (the group then shows just
 * its label).
 */
function describeStructure(subtaskCount: number, dependencyCount: number): string {
  const parts: string[] = [];
  if (subtaskCount > 0) parts.push(`${subtaskCount} sub-task${subtaskCount === 1 ? "" : "s"}`);
  if (dependencyCount > 0) parts.push(`${dependencyCount} link${dependencyCount === 1 ? "" : "s"}`);
  return parts.join(" · ");
}

// ── Small shared bits ────────────────────────────────────────────────────────

function SectionLabel({ children, theme }: { children: React.ReactNode; theme: MapTheme }) {
  const editorial = theme.mode === "editorial";
  return (
    <div
      className="text-[11px] font-semibold uppercase tracking-wide"
      style={
        editorial
          ? { color: theme.palette.muted, letterSpacing: "0.06em", fontFamily: theme.fonts.mono }
          : { color: "#6B7280", letterSpacing: "0.5px" }
      }
    >
      {children}
    </div>
  );
}

/**
 * One metadata row in the Details tab. EDITORIAL (`.t-drawer .row`): a `k`/`v`
 * pair on a single baseline-aligned line separated by a 1px DASHED rule, with a
 * mono uppercase key (the wireframe's `t-label`) and the value in ink. The key
 * labels are upper-cased in editorial ("Assigned to" → "ASSIGNED TO") to match
 * the wireframe's mono-label look — the underlying label text is unchanged.
 * LEGACY keeps the shipped solid-bottom-border row, byte-identical.
 */
function DetailRow({
  label,
  children,
  theme,
}: {
  label: string;
  children: React.ReactNode;
  theme: MapTheme;
}) {
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  if (editorial) {
    return (
      <div
        className="flex items-baseline gap-3 py-[7px]"
        style={{ borderBottom: `1px dashed ${c.rule}` }}
      >
        <span
          className="w-[100px] shrink-0 text-[10px] font-semibold uppercase"
          style={{ color: c.muted, fontFamily: theme.fonts.mono, letterSpacing: "0.06em" }}
        >
          {label}
        </span>
        <div className="min-w-0 flex-1" style={{ color: c.ink, fontSize: "12.5px" }}>
          {children}
        </div>
      </div>
    );
  }
  return (
    <div
      className="flex items-center justify-between gap-3 border-b py-2.5"
      style={{ borderColor: "#F3F4F6" }}
    >
      <span className="shrink-0 text-sm" style={{ color: PALETTE.muted }}>
        {label}
      </span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function OptionalValue({
  value,
  testId,
  theme,
}: {
  value: string | null;
  testId: string;
  theme: MapTheme;
}) {
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  return (
    <span
      className="text-sm"
      style={{
        color: value ? (editorial ? c.ink : PALETTE.ink) : editorial ? c.faint : "#9CA3AF",
        fontFamily: editorial && value ? theme.fonts.mono : undefined,
      }}
      data-testid={testId}
    >
      {value ?? "— (optional)"}
    </span>
  );
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

// ── Estimate section (B2.12, #1043 — gated `task_estimates`) ─────────────────
// The wireframe `V_B212`: a node gets coarse bucket chips (½d · 1d · 2d · 3d ·
// 1w · 2w · custom…), the selected one ink-bordered; the italic "coarse buckets
// — not a contract … roll up to the parent for display only" note; and a PARENT
// also shows the read-time sum-of-leaves roll-up `~X total · ~Y remaining`. The
// section renders ONLY when the layer is on AND editorial AND the actor can
// author (the caller gates it); otherwise the drawer keeps the display-only
// "Estimate" row, byte-unchanged. Coarse + about the WORK, never a contract or a
// per-person commitment (§A1.2 / §A9).

function EstimateSection({
  node,
  onSetEstimate,
  theme,
}: {
  node: TreeNode;
  onSetEstimate: (minutes: number | null) => void;
  theme: MapTheme;
}) {
  const c = theme.palette;
  const isLeaf = node.children.length === 0;
  // The roll-up is meaningful only on a parent (a leaf's "roll-up" is just its
  // own estimate). It is computed here at read time, never stored.
  const rollup = isLeaf ? null : estimateRollup(node);
  const [customOpen, setCustomOpen] = useState(false);
  const [customDays, setCustomDays] = useState("");

  // A custom value is "the current estimate doesn't match any preset bucket".
  const current = node.estimateMinutes;
  const matchesBucket = ESTIMATE_BUCKETS.some((b) => b.minutes === current);
  const isCustom = current != null && current > 0 && !matchesBucket;

  const commitCustom = () => {
    const days = Number(customDays);
    if (Number.isFinite(days) && days > 0) {
      onSetEstimate(Math.round(days * MINUTES_PER_DAY));
    }
    setCustomOpen(false);
    setCustomDays("");
  };

  return (
    <div data-testid="drawer-estimate-section">
      <DetailRow label="Estimate" theme={theme}>
        <div className="flex flex-wrap items-center gap-1.5" data-testid="estimate-buckets">
          {ESTIMATE_BUCKETS.map((bucket) => {
            const selected = current === bucket.minutes;
            return (
              <button
                key={bucket.label}
                type="button"
                data-testid={`estimate-bucket-${bucket.label}`}
                data-selected={selected || undefined}
                onClick={() =>
                  // Tapping the selected bucket again clears it; otherwise set it.
                  onSetEstimate(selected ? null : bucket.minutes)
                }
                className="rounded-md px-3 py-1 text-center transition-colors hover:bg-black/5"
                style={{
                  height: 28,
                  fontFamily: theme.fonts.mono,
                  fontSize: "12px",
                  color: selected ? c.ink : c.muted,
                  border: selected ? `1.5px solid ${c.ink}` : `1px solid ${c.rule}`,
                  backgroundColor: selected ? c.bgRecess : "transparent",
                }}
              >
                {bucket.label}
              </button>
            );
          })}
          <button
            type="button"
            data-testid="estimate-bucket-custom"
            data-selected={isCustom || undefined}
            onClick={() => setCustomOpen((v) => !v)}
            className="rounded-md px-3 py-1 text-center transition-colors hover:bg-black/5"
            style={{
              height: 28,
              fontFamily: theme.fonts.mono,
              fontSize: "12px",
              color: isCustom ? c.ink : c.muted,
              border: isCustom ? `1.5px solid ${c.ink}` : `1px solid ${c.rule}`,
              backgroundColor: isCustom ? c.bgRecess : "transparent",
            }}
          >
            custom…
          </button>
        </div>
      </DetailRow>

      {/* The custom-days input (a simple working-days number → minutes). */}
      {customOpen && (
        <div className="mt-2 flex items-center gap-2" data-testid="estimate-custom-input">
          <input
            type="number"
            min={0}
            step={0.5}
            value={customDays}
            onChange={(e) => setCustomDays(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitCustom();
            }}
            data-testid="estimate-custom-days"
            placeholder="days"
            className="w-20 rounded-md border px-2 py-1 text-sm"
            style={{ borderColor: c.rule, color: c.ink, fontFamily: theme.fonts.mono }}
          />
          <span className="text-[11px]" style={{ color: c.muted }}>
            working days
          </span>
          <button
            type="button"
            data-testid="estimate-custom-set"
            onClick={commitCustom}
            className="rounded-md border px-2.5 py-1 text-[11.5px] transition-colors hover:bg-black/5"
            style={{ borderColor: c.ink, color: c.ink }}
          >
            set
          </button>
        </div>
      )}

      {/* Brand-firewall note — verbatim from the wireframe `V_B212`. */}
      <p
        className="mt-2 text-[11.5px] italic"
        data-testid="estimate-note"
        style={{ color: c.muted }}
      >
        Estimates are coarse buckets — not a contract. They roll up to the parent for display only.
      </p>

      {/* Parent roll-up — the read-time sum-of-leaves (`~X total · ~Y remaining`),
          computed, never stored. Shown only on a parent (a leaf has no roll-up of
          its own beyond its single estimate). */}
      {rollup && rollup.totalMinutes > 0 && (
        <p
          className="mt-1 text-[11.5px]"
          data-testid="estimate-rollup"
          style={{ color: c.faint, fontFamily: theme.fonts.mono }}
        >
          {formatEstimate(rollup.totalMinutes)} total ·{" "}
          {formatEstimate(rollup.remainingMinutes) ?? "~0d"} remaining
        </p>
      )}
    </div>
  );
}
